import { getAuthRuntimeSessionIdentity } from '@/features/auth/services/authRuntime';
import React from 'react';
import { CheckCircle2, ClipboardCheck, Loader2, Play, ShieldCheck, Square, XCircle } from 'lucide-react';

import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { cn, useTheme } from '@/ui/theme/ThemeContext';
import { presentServerError } from '@/shared/utils/serverErrorPresentation';

import { agentMissionClient, type AgentMission, type AgentMissionTaskState } from './agentMissionClient';

interface AgentMissionSectionProps {
  projectId: string;
  canEdit: boolean;
  canExecuteAgent?: boolean;
}

const MISSION_REFRESH_INTERVAL_MS = 10_000;
const VISIBLE_TASK_LIMIT = 6;
// Mirrors the backend MISSION_TERMINAL_STATUSES: the current-mission endpoint keeps returning a
// finished mission, so the panel must offer a fresh draft instead of a dead status badge.
const MISSION_TERMINAL_STATUSES = new Set(['completed', 'rejected', 'cancelled']);
const isTerminalMission = (mission: AgentMission | null): boolean => Boolean(
  mission && (MISSION_TERMINAL_STATUSES.has(mission.status) || MISSION_TERMINAL_STATUSES.has(String(mission.derivedStatus || ''))),
);
// Mirrors the backend MISSION_STARTABLE_STATUSES. "blocked" is derived from a failed run and start
// re-dispatches the incomplete tasks, so the panel keeps offering Advance and Cancel for it.
const MISSION_STARTABLE_STATUSES = new Set(['approved', 'running', 'waiting_review', 'needs_revision', 'blocked']);
const MISSION_ACTIVE_CANCELLABLE_STATUSES = new Set(['running', 'waiting_review', 'needs_revision', 'blocked']);

const statusClass = (status?: string | null): string => {
  switch (status) {
    case 'running':
      return 'border-blue-500/40 bg-blue-500/10 text-blue-800 dark:text-blue-300';
    case 'completed':
      return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300';
    case 'waiting_review':
    case 'needs_revision':
    case 'approved':
    case 'awaiting_approval':
      return 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300';
    case 'blocked':
    case 'cancelled':
      return 'border-red-500/40 bg-red-500/10 text-red-800 dark:text-red-300';
    default:
      return 'border-zinc-500/30 bg-zinc-500/10 text-zinc-700 dark:text-zinc-300';
  }
};

const sectionState = (mission: AgentMission | null, name: 'Plan' | 'Execution' | 'Validation' | 'Handoff'): string => {
  if (!mission) return 'not_started';
  const status = mission.derivedStatus || mission.status;
  if (name === 'Plan') {
    return mission.status === 'awaiting_approval' ? 'awaiting_approval' : mission.approvedAt ? 'completed' : status;
  }
  if (name === 'Execution') {
    return mission.summary?.activeCount ? 'running' : status;
  }
  if (name === 'Validation') {
    return mission.summary?.waitingReviewCount ? 'waiting_review' : status;
  }
  return mission.summary?.isComplete ? 'completed' : status;
};

// Status labels are sentence-case translation keys ('Not started', 'Awaiting approval'); the lowercase form had no
// catalog entry, so every stage status stayed in English.
const statusLabel = (value?: string | null): string => {
  const words = String(value || 'not_started').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const taskKey = (task: AgentMissionTaskState, index: number): string => (
  String(task.nodeId || task.node_id || task.taskKey || task.task_key || index)
);

export const AgentMissionSection: React.FC<AgentMissionSectionProps> = ({ projectId, canEdit, canExecuteAgent = false }) => {
  const { theme } = useTheme();
  const { t, language } = useSettings();
  const [mission, setMission] = React.useState<AgentMission | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [acting, setActing] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const sessionIdentity = getAuthRuntimeSessionIdentity();
  const refreshSequence = React.useRef(0);
  const refreshInFlight = React.useRef<object | null>(null);
  const actionInFlight = React.useRef<object | null>(null);

  const refreshMission = React.useCallback(async (background = false) => {
    if (refreshInFlight.current || actionInFlight.current) return;
    const operation = {}; refreshInFlight.current = operation;
    const sequence = ++refreshSequence.current;
    if (!background) setLoading(true);
    try {
      const currentMission = await agentMissionClient.getCurrent(projectId);
      if (sequence !== refreshSequence.current || getAuthRuntimeSessionIdentity() !== sessionIdentity) return;
      setMission(currentMission); setError(null);
    } catch (err) {
      if (sequence === refreshSequence.current && getAuthRuntimeSessionIdentity() === sessionIdentity) setError(presentServerError(err, language, t('Failed to load Agent Mission')));
    } finally {
      if (refreshInFlight.current === operation) {
        refreshInFlight.current = null;
        if (sequence === refreshSequence.current && getAuthRuntimeSessionIdentity() === sessionIdentity) setLoading(false);
      }
    }
  }, [projectId, sessionIdentity, t, language]);

  React.useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    refreshSequence.current += 1;
    refreshInFlight.current = null; actionInFlight.current = null;
    setMission(null); setError(null); setActing(null);
    const poll = async (background: boolean) => {
      await refreshMission(background);
      if (!disposed) timer = setTimeout(() => void poll(true), MISSION_REFRESH_INTERVAL_MS);
    };
    void poll(false);
    return () => {
      disposed = true; clearTimeout(timer);
      refreshSequence.current += 1;
      refreshInFlight.current = null; actionInFlight.current = null;
    };
  }, [refreshMission]);

  const runAction = async (action: 'draft' | 'approve' | 'start' | 'cancel') => {
    if (!canEdit || actionInFlight.current) return;
    if ((action === 'start' || action === 'cancel') && !canExecuteAgent) return;
    const operation = {}; actionInFlight.current = operation;
    const sequence = ++refreshSequence.current;
    refreshInFlight.current = null;
    const ownsAction = () => actionInFlight.current === operation && sequence === refreshSequence.current && getAuthRuntimeSessionIdentity() === sessionIdentity;
    setActing(action); setLoading(false); setError(null);
    try {
      let updated: AgentMission | null = null;
      if (action === 'draft') updated = await agentMissionClient.createDraft(projectId);
      else if (mission) {
        const missionId = mission.missionId;
        if (action === 'approve') updated = await agentMissionClient.approve(projectId, missionId, { planRevision: mission.planRevision, planHash: mission.planHash });
        if (action === 'start') updated = await agentMissionClient.start(projectId, missionId);
        if (action === 'cancel') updated = await agentMissionClient.cancel(projectId, missionId);
      }
      if (ownsAction() && updated) setMission(updated);
    } catch (err) {
      if (ownsAction()) setError(presentServerError(err, language, t('Agent Mission action failed')));
    } finally {
      if (ownsAction()) { actionInFlight.current = null; setActing(null); }
    }
  };

  const tasks = mission?.taskStates || mission?.taskGraph || [];
  const status = mission?.derivedStatus || mission?.status || 'not_started';
  const isBusy = loading || acting !== null;
  const canDraftMission = !mission || isTerminalMission(mission);

  return (
    <section
      data-testid="agent-mission-section"
      className={cn('rounded-lg border p-3', theme.borderPrimary, theme.bgSecondary)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ShieldCheck size={15} className="text-blue-600 dark:text-blue-400" />
            <h3 className={cn('text-sm font-semibold', theme.textPrimary)}>{t('Agent Mission')}</h3>
            <span className={cn('rounded-full border px-2 py-0.5 text-[10px]', statusClass(status))}>
              {t(statusLabel(status))}
            </span>
          </div>
          <p className={cn('mt-1 text-xs', theme.textSecondary)}>
            {!mission
              ? t('Create a project-level mission plan before agents execute this project.')
              : isTerminalMission(mission)
                ? t('This mission has ended. Draft a new mission to plan the next agent execution for this project.')
                : t('Approved project execution with plan, checklist progress, validation, and handoff truth.')}
          </p>
        </div>
        {isBusy ? <Loader2 size={15} className="animate-spin text-blue-600 dark:text-blue-300" /> : null}
      </div>

      {error ? (
        <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 px-2.5 py-2 text-xs text-red-800 dark:text-red-200">
          {error}
        </div>
      ) : null}

      {mission?.blockers?.length ? (
        <ul aria-label={t('Blockers')} className={cn('mt-3 space-y-1 text-xs', theme.textSecondary)}>
          {mission.blockers.map((blocker, index) => (
            <li key={`${String(blocker.nodeId || blocker.taskKey || '')}-${index}`}>
              {presentServerError(blocker, language, t('Agent Mission action failed'))}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-3 grid grid-cols-2 gap-2">
        {(['Plan', 'Execution', 'Validation', 'Handoff'] as const).map((label) => (
          <div
            key={label}
            data-testid={`agent-mission-stage-${label.toLowerCase()}`}
            className={cn('rounded-md border p-2', statusClass(sectionState(mission, label)))}
          >
            <div className="text-[10px] uppercase tracking-[0.14em] opacity-75">{t(label)}</div>
            <div className="mt-1 text-xs">{t(statusLabel(sectionState(mission, label)))}</div>
          </div>
        ))}
      </div>

      {mission ? (
        <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
          <div>
            <span className={theme.textSecondary}>{t('Workflow')}</span>
            <p className={cn('mt-0.5 font-medium', theme.textPrimary)}>{mission.workflowType.replace(/_/g, ' ')}</p>
          </div>
          <div>
            <span className={theme.textSecondary}>{t('Hosts')}</span>
            <p className={cn('mt-0.5 font-medium', theme.textPrimary)}>
              {mission.producerHostId || 'agent1'} / {mission.reviewerHostId || 'agent2'}
            </p>
          </div>
          <div>
            <span className={theme.textSecondary}>{t('Tasks')}</span>
            <p className={cn('mt-0.5 font-medium', theme.textPrimary)}>
              {mission.summary?.approvedCount || 0}/{mission.summary?.taskCount || tasks.length} {t('approved')}
            </p>
          </div>
          <div>
            <span className={theme.textSecondary}>{t('Blockers')}</span>
            <p className={cn('mt-0.5 font-medium', theme.textPrimary)}>{mission.summary?.blockerCount || mission.blockers?.length || 0}</p>
          </div>
        </div>
      ) : null}

      {tasks.length > 0 ? (
        <div className="mt-3 space-y-1.5">
          {tasks.slice(0, VISIBLE_TASK_LIMIT).map((task, index) => {
            const gate = task.gate;
            const icon = gate?.state === 'approved'
              ? <CheckCircle2 size={12} />
              : gate?.state === 'blocked'
                ? <XCircle size={12} />
                : gate?.state === 'running' || gate?.state === 'reviewing'
                  ? <Loader2 size={12} className="animate-spin" />
                  : <Square size={12} />;
            return (
              <div key={taskKey(task, index)} className={cn('flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs', theme.borderPrimary)}>
                <span className={cn('shrink-0', statusClass(gate?.state).split(' ').slice(-1).join(' '))}>{icon}</span>
                <span className="min-w-0 flex-1 truncate">{task.title}</span>
                <span className={cn('shrink-0', theme.textSecondary)}>{t(statusLabel(gate?.state))}</span>
              </div>
            );
          })}
          {tasks.length > VISIBLE_TASK_LIMIT ? (
            <p data-testid="agent-mission-task-limit" className={cn('px-1 text-[10px]', theme.textSecondary)}>
              {t('Showing {{visible}} of {{total}} tasks', { visible: VISIBLE_TASK_LIMIT, total: tasks.length })}
            </p>
          ) : null}
        </div>
      ) : null}

      {canEdit ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {canDraftMission ? (
            <button
              type="button"
              data-testid="agent-mission-draft"
              disabled={isBusy}
              onClick={() => void runAction('draft')}
              className="inline-flex items-center gap-1.5 rounded-md border border-blue-500/40 bg-blue-500/10 px-2.5 py-1.5 text-xs text-blue-800 dark:text-blue-200 disabled:opacity-60"
            >
              <ClipboardCheck size={13} /> {t(mission ? 'Draft new mission' : 'Draft mission')}
            </button>
          ) : null}
          {mission?.status === 'awaiting_approval' ? (
            <button
              type="button"
              data-testid="agent-mission-approve"
              disabled={isBusy}
              onClick={() => void runAction('approve')}
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-800 dark:text-amber-200 disabled:opacity-60"
            >
              <ShieldCheck size={13} /> {t('Approve plan')}
            </button>
          ) : null}
          {canExecuteAgent && mission && MISSION_STARTABLE_STATUSES.has(mission.status) ? (
            <button
              type="button"
              data-testid="agent-mission-start"
              disabled={isBusy}
              onClick={() => void runAction('start')}
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-xs text-emerald-800 dark:text-emerald-200 disabled:opacity-60"
            >
              <Play size={13} /> {t(mission.status === 'approved' ? 'Start mission' : 'Advance mission')}
            </button>
          ) : null}
          {canExecuteAgent && mission && MISSION_ACTIVE_CANCELLABLE_STATUSES.has(mission.status) ? (
            <button
              type="button"
              data-testid="agent-mission-cancel"
              disabled={isBusy}
              onClick={() => void runAction('cancel')}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-2.5 py-1.5 text-xs text-red-800 dark:text-red-200 disabled:opacity-60"
            >
              <XCircle size={13} /> {t('Cancel mission')}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};
